import { UTCDate } from "@date-fns/utc";
import { eachMonthOfInterval, eachWeekOfInterval, endOfDay, endOfMonth, endOfWeek, format, startOfDay } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { ROLES } from "../../../../../constnats/user";
import CollectionUtils from "../../../../../utils/collections";
import AcrivityLogRepo from "../../../../../utils/repository/activityLogRepo";
import TableUpdatesRepo from "../../../../../utils/repository/tableUpdatesRepo";
import UserRepo from "../../../../../utils/repository/userRepo";
import { getTableModel } from "../../../activityLogs/utils";
import { adjustedUserGroupedMistakeKPIs, getRawUserGroupedMistakeKPIs } from "../../utils/qualityUtils";
import { ConfigProps, DEFAULT_CONFIG } from "../productivity/constants";
import { getQuantityPerformance, getTableData, getTableTypes } from "../productivity/utils";
import { activityIgnoredUsers, INTERVAL_TYPE, StatisticProps } from "./constants";
import CloneUtils from "../../../../../utils/cloneUtils";


class Cache {

    constructor() {
        this._cachedData = {}
    }

    /**
     * @param {any} dataProviderArgs arguments to be passed to the data provided to retrieve the data when needed. Required
     * @param {function} dataProvider Data provider data accepts the "dataProviderArgs" and a data consumer. Required
     * @param {function} dataConsumer Data consumer used by the client to retrieve the requested data.
     * 
     * NOTE: The properties (if any) of the dataProviderArgs must be ordered consistently in to allow the cache to work properly.
     */
    get(dataProviderArgs, dataProvider, dataConsumer) {

        const keyStr = JSON.stringify(dataProviderArgs) + "__" + dataProvider

        if (this._cachedData[keyStr] !== undefined) {
            dataConsumer(CloneUtils.deepClone(this._cachedData[keyStr]))
        } else {
            dataProvider(dataProviderArgs, data => {
                this._cachedData[keyStr] = data
                dataConsumer(CloneUtils.deepClone(data))
            })
        }
    }
}

const TASKS_EXECUTOR_CACHE = new Cache()

export function getIntervals(startDateUTC, endDateUTC, intervalType) {
    let intervals = []
    if (intervalType === INTERVAL_TYPE.MONTHLY) {
        intervals = getMonthlyIntervals(startDateUTC, endDateUTC)
    } else if (intervalType === INTERVAL_TYPE.WEEKLY) {

        intervals = getWeeklyIntervals(startDateUTC, endDateUTC);
    }
    return intervals
}

function getWeeklyIntervals(startDate, endDate) {
    // Using eachWeekOfInterval to get the start of each week
    const weeklyIntervals = [];
    const startOfEachWeek = eachWeekOfInterval(
        { start: startDate, end: endDate },
    );

    // For each start date, create a weekly interval
    startOfEachWeek.forEach((weekStart) => {
        // The end date is 6 days after the start
        const weekEnd = endOfWeek(weekStart);

        weeklyIntervals.push({
            startDate: startOfDay(weekStart).getTime(),
            endDate: endOfDay(weekEnd).getTime(),
        });
    });

    return weeklyIntervals;
}

function getMonthlyIntervals(startDate, endDate) {
    const monthlyIntervals = [];
    const startOfEachMonth = eachMonthOfInterval({ start: startDate, end: endDate });

    startOfEachMonth.forEach(monthStart => {
        const monthEnd = endOfMonth(monthStart);
        monthlyIntervals.push({
            startDate: startOfDay(monthStart).getTime(),
            endDate: endOfDay(monthEnd).getTime()
        })
    })

    return monthlyIntervals
}

export function useUsersMap() {
    const [usersMap, setUsersMap] = useState(null);
    useEffect(() => {
        UserRepo.list({ userRoles: [ROLES.EMPLOYEE], includeDisabled: true }, (users) =>
            setUsersMap(CollectionUtils.arrToMap(users, (u) => u.id))
        );
    }, []);
    return usersMap;
}

function sequentialTasksExecutor(executor, params, singleTaskProcessor, resultProcessor) {
    let currentParamIndex = 0;
    const tasksResults = []

    function makeApiCall() {
        if (currentParamIndex < params.length) {
            const param = params[currentParamIndex]
            TASKS_EXECUTOR_CACHE.get(param, executor, (data) => {
                tasksResults.push(singleTaskProcessor(data, param))
                currentParamIndex += 1;
                makeApiCall()
            })
        } else {
            resultProcessor(tasksResults)
        }
    }

    makeApiCall()
}

export function useProductivity(usersMap, intervals) {
    const [loading, setLoading] = useState(true);
    const [performanceArray, setPerformanceArray] = useState([]);
    const [allTableTypes, setAllTableTypes] = useState([]);
    const [config, setConfig] = useState({ ...DEFAULT_CONFIG });

    useEffect(() => {
        setLoading(true);
        let tableTypes = [];
        const params = intervals.map(interval => ({ from: interval.startDate, to: interval.endDate, includeDataWithUnknownMistakes: true, }))
        sequentialTasksExecutor(TableUpdatesRepo.performanceKpisV2, params, (performance, param) => {
            tableTypes = Array.from(new Set([...tableTypes, ...getTableTypes(performance)]));
            return {
                endDate: param.to,
                performance,
            }
        }, (performanceArray) => {
            setPerformanceArray(performanceArray);
            setConfig((prev) => ({ ...prev, [ConfigProps.TABLE_TYPES]: tableTypes }));
            setAllTableTypes(tableTypes);
            setLoading(false);
        })

        return () => {
            setPerformanceArray([]);
            setAllTableTypes([])
            setConfig((prev) => ({ ...prev, [ConfigProps.TABLE_TYPES]: [] }))
        }
    }, [intervals]);

    const productivityData = useMemo(() => {
        return performanceArray.map(({ performance, endDate }) => {
            const userToMistakesKPIs = getRawUserGroupedMistakeKPIs(
                Object.keys(usersMap),
                performance,
                config[ConfigProps.TABLE_TYPES]
            );

            const mistakeAjustedKPIs = CollectionUtils.arrToMap(
                adjustedUserGroupedMistakeKPIs(userToMistakesKPIs),
                (userKips) => userKips.userId
            );

            const tableData = getTableData(getQuantityPerformance(performance, usersMap, config, mistakeAjustedKPIs));

            return {
                endDate,
                total: tableData.find((x) => x.userId === 'TOTAL'),
            };
        }).reduce((acc, curr) => {
            acc[curr.endDate] = curr;
            return acc
        }, {});
    }, [performanceArray, usersMap, config]);

    return [productivityData, loading, allTableTypes, config, setConfig]
}

export function useActivity(intervals) {
    const [activity, setActivity] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const params = intervals.map(interval => ({ from: interval.startDate, to: interval.endDate }));
        sequentialTasksExecutor(AcrivityLogRepo.list, params, (activityLogs, param) => {
            activityLogs.sort((a, b) => {
                return new Date(b.start) - new Date(a.start);
            });
            return {
                endDate: param.to,
                logs: activityLogs.filter(log => !activityIgnoredUsers.includes(log.user.id)),
            };
        }, (activityLogsArray) => {
            const activity = activityLogsArray.map(({ logs, endDate }) => {
                const usernames = Array.from(new Set(logs.map((l) => l.user.fullName))).sort();

                const { middleDuration, totalClicks, totalKeystrokes } = getTableModel(logs, usernames);

                return {
                    endDate,
                    total: {
                        totalClicks,
                        totalKeystrokes,
                        actualWorkingHours: middleDuration / 60,
                    },
                }
            }).reduce((acc, curr) => {
                acc[curr.endDate] = curr
                return acc
            }, {});
            setActivity(activity);
            setLoading(false);
        })
        return () => {
            setActivity({})
        }
    }, [intervals]);


    return [activity, loading]
}

export function createStatisticsArray(productivity, activity) {
    return Object.values(productivity).map(({ endDate }) => {
        const totalScore = productivity[endDate].total[StatisticProps.totalScore.propName]
        const qaScore = productivity[endDate].total[StatisticProps.visualChecks.propName]

        /** middle duration is actual working minutes */
        const actualWorkingHours = activity[endDate].total[StatisticProps.actualWorkingHours.propName]
        let totalScoreToActualWorkingHoursRatio = 0
        if (actualWorkingHours) {
            totalScoreToActualWorkingHoursRatio = totalScore / actualWorkingHours
        }

        const declaredWorkingHours = getIntervalDeclaredWorkingHours(endDate);
        const actualToDeclaredWorkingHoursRatio = calcRatio(actualWorkingHours, declaredWorkingHours.total);
        const totalScoreToTotalDeclaredWorkingHoursRatio = calcRatio(totalScore, declaredWorkingHours.total);
        const qaScoreToDeclaredQaWorkingHoursRatio = calcRatio(qaScore, declaredWorkingHours.qa);
        const fillerScoreToDeclaredFillerWorkingHoursRatio = calcRatio(totalScore - qaScore, declaredWorkingHours.filler);

        return {
            endDate,
            total: {
                ...productivity[endDate].total,
                ...activity[endDate].total,
                totalScoreToActualWorkingHoursRatio,
                declaredWorkingHours: declaredWorkingHours.total,
                actualToDeclaredWorkingHoursRatio,
                totalScoreToTotalDeclaredWorkingHoursRatio,
                qaScoreToDeclaredQaWorkingHoursRatio,
                fillerScoreToDeclaredFillerWorkingHoursRatio
            }
        }
    }, {})
}

/**
 * 
 * @param {number} numerator required
 * @param {number} denominator  optional
 */
function calcRatio(numerator, denominator) {
    let res = 0;
    if (denominator) {
        res = numerator / denominator
    }
    return res
}

export function getChartData(statisticArray, propsToPlotOnChart) {
    const labelsRow = ['Time', ...propsToPlotOnChart.map(p => StatisticProps[p].label)];

    const chartData = [labelsRow];

    statisticArray.forEach((data, i) => {
        const row = [format(new UTCDate(data.endDate), "MMM do, yyyy")];

        propsToPlotOnChart.forEach((prop) => {
            row.push(data.total[prop]);
        });

        chartData.push(row);
    });


    return chartData;
}

function getIntervalDeclaredWorkingHours(endDate) {
    const intervalsToWorkingMinutesMap = {
        1711929599999: { filler: 113790, qa: 12780 },
        1714521599999: { filler: 145350, qa: 13410 },
        1717199999999: { filler: 135570, qa: 14520 },
        1719791999999: { filler: 128160, qa: 16680 },
        1722470399999: { filler: 165690, qa: 21120 },
        1725148799999: { filler: 152970, qa: 24960 },
        1727740799999: { filler: 131310, qa: 10800 },
        1730419199999: { filler: 175275, qa: 18900 },
        1733011199999: { filler: 140055, qa: 21180 },
        1735689599999: { filler: 109950, qa: 10905 },
        1738367999999: { filler: 130383, qa: 12837 },
        1740787199999: { filler: 117285, qa: 9855 },
        1743465599999: { filler: 109230, qa: 12240 },
        1746057599999: { filler: 108000, qa: 14385 },
        1748735999999: { filler: 92910, qa: 20805 },
    }
    let data = intervalsToWorkingMinutesMap[endDate]
    if (data === undefined) {
        console.log(endDate)
        data = { filler: 0, qa: 0 }
    }

    /** Divide by 60 to get the hours */
    data.filler = data.filler / 60
    data.qa = data.qa / 60
    data.total = data.filler + data.qa

    return data
}
